import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, ArrowUpRight, Clock, ListChecks, Loader2, Target } from "lucide-react";

import { Container } from "@/platform/layout/container";
import { Badge } from "@/platform/ui/badge";
import { Button } from "@/platform/ui/button";
import { useSession } from "@/platform/auth/use-session";
import { Blob, SoftStar } from "@/platform/ui/illustrations/blobs";
import { generateUspPlan } from "@/tenants/usp/features/study-plan/api";

export function UspPlanoPage() {
  const { session } = useSession();
  const query = useQuery({
    queryKey: ["usp", "plan"],
    queryFn: () => generateUspPlan(session!.user.id),
    enabled: !!session,
  });

  const plan = query.data;

  return (
    <div className="relative overflow-hidden bg-mesh min-h-[calc(100vh-56px)]">
      <Blob color="sky" className="pointer-events-none absolute -left-32 -top-20 w-96 opacity-20 blur-2xl animate-blob" />
      <SoftStar color="mint" className="pointer-events-none absolute right-12 top-32 w-12 opacity-50 animate-float" />

      <Container size="default" className="relative py-12 md:py-16">
        <Link to="/usp/painel" className="inline-flex items-center gap-1.5 text-[length:var(--text-body-sm)] text-[color:var(--color-muted-foreground)] hover:text-[color:var(--color-foreground)]">
          <ArrowLeft className="size-3.5" />
          Painel
        </Link>

        <header className="mt-8 max-w-2xl">
          <Badge variant="accentSolid" className="gap-2">
            <ListChecks className="size-3" />
            Plano adaptativo Fuvest
          </Badge>
          <h1 className="mt-5 font-display text-[length:var(--text-display-md)] font-semibold leading-tight tracking-[var(--text-display-md--letter-spacing)] text-balance">
            Sua semana priorizada.
          </h1>
          <p className="mt-3 text-[length:var(--text-body)] text-[color:var(--color-muted-foreground)] leading-relaxed">
            Prioridade = peso na prova × sua lacuna × peso do curso. Disciplinas com mais retorno
            sobre esforço vêm primeiro.
          </p>
        </header>

        {query.isLoading ? (
          <Loader2 className="mt-10 size-6 animate-spin text-[color:var(--color-muted-foreground)]" />
        ) : !plan ? (
          <p className="mt-10 text-[color:var(--color-muted-foreground)]">Não foi possível gerar o plano.</p>
        ) : (
          <>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Badge variant="outline" className="gap-1.5">
                <Clock className="size-3" />
                {plan.totalHours}h/semana
              </Badge>
              {!plan.basedOnSessionId && (
                <Badge variant="outline" className="gap-1.5 text-[color:var(--color-warning)]">
                  Sem simulado ainda — plano usa pesos médios
                </Badge>
              )}
            </div>

            {!plan.basedOnSessionId && (
              <div className="mt-6 rounded-xl border-2 border-[--color-accent]/30 bg-[color:var(--color-accent-soft)] p-5">
                <p className="text-[length:var(--text-body-sm)] font-medium">
                  Faça um simulado da 1ª fase para calibrar o plano com seu desempenho real.
                </p>
                <Button asChild size="sm" variant="primary" className="mt-3">
                  <Link to="/usp/1afase">
                    Fazer simulado
                    <ArrowUpRight className="size-4" />
                  </Link>
                </Button>
              </div>
            )}

            <ol className="mt-8 space-y-3">
              {plan.blocks.map((b, i) => (
                <li
                  key={b.discipline}
                  className="flex items-center gap-4 rounded-2xl border border-[--color-border] bg-[color:var(--color-card)] p-5 shadow-[var(--shadow-sm)]"
                >
                  <span
                    data-tabular="true"
                    className="grid size-9 shrink-0 place-items-center rounded-full font-display font-bold text-white"
                    style={{
                      backgroundColor: "var(--color-accent)",
                      opacity: 1 - i * 0.06,
                    }}
                  >
                    {i + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-3">
                      <span className="font-display text-[length:var(--text-body-lg)] font-semibold">{b.label}</span>
                      <span data-tabular="true" className="text-[length:var(--text-body-sm)] font-medium" style={{ color: "var(--color-accent-deep)" }}>
                        {b.suggestedHours}h
                      </span>
                    </div>
                    <p className="mt-0.5 text-[length:var(--text-caption)] text-[color:var(--color-muted-foreground)]">
                      {b.reason}
                      {b.accuracy !== null && ` · acerto atual ${Math.round(b.accuracy * 100)}%`}
                    </p>
                    <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-[color:var(--color-muted)]">
                      <div className="h-full rounded-full" style={{ width: `${b.priority}%`, backgroundColor: "var(--color-accent)" }} />
                    </div>
                  </div>
                </li>
              ))}
            </ol>

            <div className="mt-8 flex flex-wrap gap-3">
              <Button asChild variant="primary">
                <Link to="/usp/1afase">
                  <Target className="size-4" />
                  Praticar agora
                </Link>
              </Button>
              <Button asChild variant="outline">
                <Link to="/usp/revisar">Revisar pendências</Link>
              </Button>
            </div>
          </>
        )}
      </Container>
    </div>
  );
}
